/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import Avatar from '@atlaskit/avatar/avatar';
import Button from '@atlaskit/button/default/button';
import { cssMap, jsx } from '@atlaskit/css';
import type MediaBelowSm from '@atlaskit/css/at-rules/media-below-sm';
import Heading from '@atlaskit/heading/heading';
import ModalIcon from '@atlaskit/icon-lab/core/modal';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import TaskIcon from '@atlaskit/icon/core/task';
import { JiraIcon } from '@atlaskit/logo/jira-icon';
import Lozenge from '@atlaskit/lozenge/lozenge';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { PanelActionClose } from '@atlassian/panel-system/panel-action/close';
import { PanelActionDown } from '@atlassian/panel-system/panel-action/down';
import { PanelActionGroup } from '@atlassian/panel-system/panel-action/group';
import { PanelActionUp } from '@atlassian/panel-system/panel-action/up';
import {
	PanelActionViewSwitcher,
	type PanelViewOption,
} from '@atlassian/panel-system/panel-action/view-switcher';
import { PanelBody } from '@atlassian/panel-system/panel-body';
import { PanelContainer } from '@atlassian/panel-system/panel-container';
import { PanelContent } from '@atlassian/panel-system/panel-content';
import { PanelHeader } from '@atlassian/panel-system/panel-header';
import { PanelSubheader } from '@atlassian/panel-system/panel-subheader';
import { PanelTitle } from '@atlassian/panel-system/panel-title';

const issues = [
	{
		key: 'CAT-991',
		summary: 'Prepare imagery for the content update',
		status: 'Done',
		assignee: 'Alex Morgan',
		priority: 'Minor',
		description:
			'Refresh the Foundations imagery so the documentation reflects the latest design language.',
		done: 'Updated images are reviewed and ready to publish with the documentation release.',
	},
	{
		key: 'CAT-997',
		summary: 'Update the Figma component playground',
		status: 'In progress',
		assignee: 'Sam Chen',
		priority: 'Major',
		description:
			'Bring the component playground up to date with the latest panel actions and layout patterns.',
		done: 'Designers can explore the new actions in the playground and find the matching code examples.',
	},
	{
		key: 'CAT-1042',
		summary: 'Review the visual design of work item details',
		status: 'To do',
		assignee: 'Jordan Lee',
		priority: 'Major',
		description:
			'Review the hierarchy, spacing, and status treatment in the work item details panel.',
		done: 'The review covers desktop and narrow layouts, and feedback is captured for implementation.',
	},
	{
		key: 'CAT-1074',
		summary: 'Improve the empty state for filtered lists',
		status: 'In progress',
		assignee: 'Taylor Reed',
		priority: 'Minor',
		description: 'Help people understand when no work matches their filters and how to recover.',
		done: 'The empty state explains the result and offers a clear way to adjust the filters.',
	},
	{
		key: 'CAT-992',
		summary: 'Document responsive typography decisions',
		status: 'Done',
		assignee: 'Casey Patel',
		priority: 'Minor',
		description: 'Record the typography decisions used across list views and contextual panels.',
		done: 'Guidance includes readable examples at desktop, narrow widths, and increased zoom.',
	},
	{
		key: 'CAT-1075',
		summary: 'Plan the next design system milestone',
		status: 'To do',
		assignee: 'Alex Morgan',
		priority: 'Major',
		description:
			'Bring together the next set of component improvements and agree on the delivery order.',
		done: 'Each work item has an owner, a clear outcome, and a place in the milestone.',
	},
] as const;

const views: readonly PanelViewOption[] = [
	{ value: 'sidebar', label: 'Side', icon: PanelRightIcon },
	{ value: 'center', label: 'Center', icon: ModalIcon },
];

const activity = [
	[
		'Scope confirmed',
		'The team agreed on the expected outcome and the work needed for this item. The description reflects the latest scope.',
	],
	[
		'Design review',
		'Reviewed the proposed experience against the design system. Follow-up notes cover spacing, content hierarchy, and responsive layouts.',
	],
	[
		'Accessibility review',
		'Checked keyboard navigation, focus order, and accessible names. The implementation should preserve context when moving between work items.',
	],
	[
		'Implementation started',
		'The first version is ready for a closer look. The team is checking the main flow and capturing any edge cases.',
	],
	[
		'Feedback incorporated',
		'Updated the work based on review comments. The revised approach keeps the interaction consistent with nearby experiences.',
	],
	[
		'Validation planned',
		'Validation covers desktop and narrow layouts, keyboard use, and moving between related work items. Results will be recorded here.',
	],
	[
		'Documentation reviewed',
		'The example and supporting guidance describe the intended behavior. Reviewers can use them to try the workflow themselves.',
	],
	[
		'Next steps agreed',
		'The owner will follow up on outstanding feedback and share the next update with the team. The definition of done remains the completion checklist.',
	],
] as const;

const styles = cssMap({
	layout: {
		display: 'flex',
		height: '100vh',
		backgroundColor: token('elevation.surface'),
		['@media not all and (min-width: 48rem)' satisfies MediaBelowSm]: {
			flexDirection: 'column',
			height: 'auto',
			minHeight: '100vh',
		},
	},
	main: {
		overflow: 'auto',
		flexGrow: 1,
		minWidth: 0,
		paddingBlockStart: token('space.300'),
		paddingBlockEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
	},
	listHeading: {
		paddingBlockEnd: token('space.150'),
		borderBlockEndColor: token('color.border.selected'),
		borderBlockEndStyle: 'solid',
		borderBlockEndWidth: token('border.width.selected'),
	},
	tableRegion: {
		overflowX: 'auto',
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.small'),
	},
	table: {
		width: '100%',
		minWidth: '640px',
		borderCollapse: 'collapse',
	},
	cell: {
		paddingBlockStart: token('space.150'),
		paddingBlockEnd: token('space.150'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
		textAlign: 'start',
		borderBlockEndColor: token('color.border'),
		borderBlockEndStyle: 'solid',
		borderBlockEndWidth: token('border.width'),
	},
	columnHeading: {
		backgroundColor: token('elevation.surface.sunken'),
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.semibold'),
	},
	summary: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) 28px',
		alignItems: 'center',
		columnGap: token('space.150'),
	},
	selectionSlot: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '28px',
		height: '28px',
	},
	selectionIndicator: {
		display: 'inline-flex',
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.050'),
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.050'),
		backgroundColor: token('color.background.selected'),
		borderColor: token('color.border.selected'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.small'),
	},
	panel: {
		height: '100%',
		width: '380px',
		maxWidth: '100%',
		flexShrink: 0,
		borderInlineStartColor: token('color.border'),
		borderInlineStartStyle: 'solid',
		borderInlineStartWidth: token('border.width'),
		['@media not all and (min-width: 48rem)' satisfies MediaBelowSm]: {
			width: '100%',
			height: '80vh',
			borderInlineStartWidth: 0,
			borderBlockStartColor: token('color.border'),
			borderBlockStartStyle: 'solid',
			borderBlockStartWidth: token('border.width'),
		},
	},
	panelRegion: {
		height: '100%',
		minHeight: 0,
	},
	modalHeader: {
		width: '100%',
		minWidth: 0,
	},
	stickyRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		width: '100%',
		minWidth: 0,
	},
	stickyTitle: {
		flexGrow: 1,
		minWidth: 0,
	},
	stickyStatus: {
		flexShrink: 0,
	},
	activityItem: {
		paddingBlockEnd: token('space.200'),
		borderBlockEndColor: token('color.border'),
		borderBlockEndStyle: 'solid',
		borderBlockEndWidth: token('border.width'),
	},
	panelBody: {
		paddingBlockEnd: token('space.300'),
	},
	details: {
		display: 'grid',
		gridTemplateColumns: '100px minmax(0, 1fr)',
		alignItems: 'center',
		columnGap: token('space.200'),
		rowGap: token('space.200'),
		marginBlockStart: 0,
		marginBlockEnd: 0,
	},
	value: {
		marginInlineStart: 0,
	},
});

function IssueStatus({ status }: { status: (typeof issues)[number]['status'] }) {
	return (
		<Lozenge
			appearance={
				status === 'Done' ? 'success' : status === 'In progress' ? 'inprogress' : 'default'
			}
		>
			{status}
		</Lozenge>
	);
}

/**
 * The host list owns selection and ordering. Navigation replaces the current issue's
 * content without remounting the panel or moving focus away from its action buttons.
 */
export default function JiraListNavigationExample(): JSX.Element {
	const [selectedIndex, setSelectedIndex] = useState<number | null>(1);
	const [selectedView, setSelectedView] = useState('sidebar');
	const returnFocus = useRef<HTMLElement | null>(null);
	const issue = selectedIndex === null ? null : issues[selectedIndex];
	const issueButtons = useRef(new Map<string, HTMLButtonElement>());
	const sidePanelRef = useRef<HTMLDivElement | null>(null);
	const centerPanelRef = useRef<HTMLDivElement>(null);
	const focusPanelOnSelection = useRef(false);
	const setSidePanelRef = useCallback((element: HTMLDivElement | null) => {
		sidePanelRef.current = element;
		if (element) {
			returnFocus.current = element;
		}
	}, []);

	useEffect(() => {
		if (focusPanelOnSelection.current) {
			sidePanelRef.current?.focus();
			focusPanelOnSelection.current = false;
		}
	}, [selectedIndex]);

	const selectIssue = (index: number) => {
		setSelectedView('sidebar');
		if (selectedIndex === index) {
			sidePanelRef.current?.focus();
		} else {
			focusPanelOnSelection.current = true;
			setSelectedIndex(index);
		}
	};

	const closePanel = () => {
		returnFocus.current = issue ? (issueButtons.current.get(issue.key) ?? null) : null;
		if (selectedView === 'sidebar') {
			returnFocus.current?.focus();
		}
		setSelectedIndex(null);
	};

	// This example wraps at either end, so both navigation actions remain available.
	const navigate = (direction: -1 | 1) => {
		setSelectedIndex((current) =>
			current === null ? current : (current + direction + issues.length) % issues.length,
		);
	};

	const navigationActions = (
		<PanelActionGroup>
			<PanelActionUp label="Previous work item" onClick={() => navigate(-1)} />
			<PanelActionDown label="Next work item" onClick={() => navigate(1)} />
			<PanelActionViewSwitcher
				views={views}
				selectedView={selectedView}
				onViewChange={setSelectedView}
			/>
			{selectedView === 'sidebar' && (
				<PanelActionClose label="Close work item details" onClick={closePanel} />
			)}
		</PanelActionGroup>
	);

	const panel = issue && selectedIndex !== null && (
		<Box
			id="jira-list-issue-panel"
			ref={selectedView === 'sidebar' ? setSidePanelRef : centerPanelRef}
			role="region"
			aria-label={`Work item details: ${issue.summary}`}
			tabIndex={-1}
			xcss={styles.panelRegion}
			onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
				if (
					event.key === 'Escape' &&
					selectedView === 'sidebar' &&
					!event.defaultPrevented &&
					!event.currentTarget.querySelector('[role="menu"]')
				) {
					event.stopPropagation();
					closePanel();
				}
			}}
		>
			<PanelContainer>
				{selectedView === 'center' ? (
					<ModalHeader hasCloseButton closeButtonLabel="Close work item details">
						<Flex
							alignItems="center"
							justifyContent="space-between"
							gap="space.200"
							xcss={styles.modalHeader}
						>
							<ModalTitle>Jira</ModalTitle>
							{navigationActions}
						</Flex>
					</ModalHeader>
				) : (
					<PanelHeader>
						<PanelTitle icon={<JiraIcon size="xsmall" />}>Jira</PanelTitle>
						{navigationActions}
					</PanelHeader>
				)}
				<PanelContent testId="jira-list-panel-content">
					<PanelSubheader
						title={issue.summary}
						titleIcon={<TaskIcon label="" />}
						breadcrumbs={
							<div role="status" aria-live="polite" aria-atomic="true">
								<Text size="small" color="color.text.subtle">
									{selectedIndex + 1} of {issues.length} · {issue.key}
								</Text>
							</div>
						}
						stickyContent={
							<div css={styles.stickyRow}>
								<Box xcss={styles.stickyTitle}>
									<Text maxLines={1} weight="semibold">
										{issue.key} · {issue.summary}
									</Text>
								</Box>
								<Box xcss={styles.stickyStatus}>
									<IssueStatus status={issue.status} />
								</Box>
							</div>
						}
						testId="jira-list-subheader"
					/>
					<PanelBody>
						<Box xcss={styles.panelBody}>
							<Stack space="space.300">
								<Box>
									<IssueStatus status={issue.status} />
								</Box>
								<Stack space="space.100">
									<Heading size="small" as="h3">
										Description
									</Heading>
									<Text>{issue.description}</Text>
								</Stack>
								<Stack space="space.100">
									<Heading size="small" as="h3">
										Definition of done
									</Heading>
									<Text>{issue.done}</Text>
								</Stack>
								<Stack space="space.200">
									<Heading size="small" as="h3">
										Details
									</Heading>
									<dl css={styles.details}>
										<dt>Assignee</dt>
										<dd css={styles.value}>
											<Inline space="space.100" alignBlock="center">
												<Avatar size="small" name={issue.assignee} />
												<Text>{issue.assignee}</Text>
											</Inline>
										</dd>
										<dt>Priority</dt>
										<dd css={styles.value}>{issue.priority}</dd>
										<dt>Project</dt>
										<dd css={styles.value}>Design system</dd>
									</dl>
								</Stack>
								<Stack space="space.200">
									<Heading size="small" as="h3">
										Activity
									</Heading>
									{activity.map(([title, description]) => (
										<Box key={title} xcss={styles.activityItem}>
											<Stack space="space.100">
												<Text weight="semibold">{title}</Text>
												<Text size="small" color="color.text.subtle">
													{issue.assignee} · {issue.key}
												</Text>
												<Text>{description}</Text>
											</Stack>
										</Box>
									))}
								</Stack>
							</Stack>
						</Box>
					</PanelBody>
				</PanelContent>
			</PanelContainer>
		</Box>
	);

	return (
		<Box xcss={styles.layout}>
			<Box as="main" xcss={styles.main}>
				<Stack space="space.300">
					<Stack space="space.100">
						<Text size="small" color="color.text.subtle">
							Spaces / Design system
						</Text>
						<Heading size="large" as="h1">
							ADS Components &amp; Architecture
						</Heading>
					</Stack>
					<Box xcss={styles.listHeading}>
						<Text weight="semibold">List</Text>
					</Box>
					<Text>
						Open a work item, then use the panel's previous and next actions to browse the list.
						Navigation wraps at the first and last item. Switch between Side and Center views, or
						scroll the details to see the sticky subheader.
					</Text>
					<div role="region" aria-label="Work items" css={styles.tableRegion}>
						<table css={styles.table} aria-label="Design system work items">
							<thead>
								<tr>
									{['Work', 'Summary', 'Status', 'Priority', 'Assignee'].map((column) => (
										<th key={column} scope="col" css={[styles.cell, styles.columnHeading]}>
											{column}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{issues.map((item, index) => (
									<tr key={item.key}>
										<td css={styles.cell}>
											<Button
												ref={(element) => {
													if (element) {
														issueButtons.current.set(item.key, element);
													} else {
														issueButtons.current.delete(item.key);
													}
												}}
												appearance="subtle"
												spacing="compact"
												iconBefore={TaskIcon}
												aria-label={`Open ${item.key}: ${item.summary}`}
												aria-expanded={selectedIndex === index}
												aria-controls={
													selectedIndex === index ? 'jira-list-issue-panel' : undefined
												}
												onClick={() => selectIssue(index)}
											>
												{item.key}
											</Button>
										</td>
										<td css={styles.cell}>
											<div css={styles.summary}>
												<span>{item.summary}</span>
												<span css={styles.selectionSlot}>
													{selectedIndex === index && (
														<span css={styles.selectionIndicator}>
															<PanelRightIcon
																label="Currently open in details panel"
																color={token('color.icon.selected')}
															/>
														</span>
													)}
												</span>
											</div>
										</td>
										<td css={styles.cell}>
											<IssueStatus status={item.status} />
										</td>
										<td css={styles.cell}>{item.priority}</td>
										<td css={styles.cell}>{item.assignee}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<Text size="small" color="color.text.subtle">
						{issues.length} work items
					</Text>
				</Stack>
			</Box>
			{issue && selectedView === 'sidebar' && <Box xcss={styles.panel}>{panel}</Box>}
			<ModalTransition>
				{issue &&
					selectedView === 'center' && (
						// The shared panel renders ModalHeader with its standard close button.
						// eslint-disable-next-line @atlaskit/design-system/use-modal-dialog-close-button
						<Modal
							width="large"
							height="80vh"
							onClose={closePanel}
							autoFocus={centerPanelRef}
							shouldReturnFocus={returnFocus}
						>
							{panel}
						</Modal>
					)}
			</ModalTransition>
		</Box>
	);
}
